import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { FaEye, FaEyeSlash, FaEnvelope, FaLock, FaUser } from "react-icons/fa"
import { getAuth, signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth"
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore"
import { db } from "../firebase"
import { useAuth } from "./AuthContext"
import SignupModal from "./SignupModal"
import AccountLinkingModal from "./AccountLinkingModal"

const Login = () => {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [forgotEmail, setForgotEmail] = useState("")
  const [forgotError, setForgotError] = useState("")
  const [resetMessage, setResetMessage] = useState("")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false)
  const [isLinkingModalOpen, setIsLinkingModalOpen] = useState(false)
  const [authMethods, setAuthMethods] = useState([])
  const navigate = useNavigate()
  const { setCurrentUser, setRole, setLibraryId, googleSignIn, pendingEmail } = useAuth()

  const handleLogin = async (e) => {
    e.preventDefault()
    setError("")
    setResetMessage("")
    setIsLoading(true)

    if (!email || !password) {
      setError("Please enter both email and password.")
      setIsLoading(false)
      return
    }

    if (password.length < 6) {
      setError("Password should be at least 6 characters long.")
      setIsLoading(false)
      return
    }

    try {
      const userCredential = await signInWithEmailAndPassword(getAuth(), email, password)
      const user = userCredential.user

      const userDoc = await getDoc(doc(db, "users", user.uid))
      if (userDoc.exists()) {
        const userData = userDoc.data()
        const { role, libraryId } = userData

        if (!role) {
          setError("Role not found. Please contact support.")
          setIsLoading(false)
          return
        }

        setCurrentUser(user)
        setRole(role)
        setLibraryId(libraryId || "")

        localStorage.setItem("userId", user.uid)
        localStorage.setItem("role", role)
        localStorage.setItem("libraryId", libraryId || "")

        if (role === "admin") {
          navigate("/admin-dashboard")
        } else if (role === "owner") {
          navigate("/owner-dashboard")
        } else {
          navigate("/dashboard")
        }
      } else {
        setError("User data not found.")
        setIsLoading(false)
      }
    } catch (err) {
      console.error("Login failed:", err)
      setError("Invalid email or password. Please try again.")
      setIsLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setError("")
    setResetMessage("")
    setIsLoading(true)

    try {
      const result = await googleSignIn()

      // Handle account linking case
      if (result.error === "auth/account-exists-with-different-credential") {
        setAuthMethods(result.methods)
        setEmail(result.email)
        setIsLinkingModalOpen(true)
        setIsLoading(false)
        return
      }

      // Normal successful sign-in
      const { user, role, libraryId } = result

      // Check if user document exists in Firestore
      const userDocRef = doc(db, "users", user.uid)
      const userDoc = await getDoc(userDocRef)

      // If user document doesn't exist, create it
      if (!userDoc.exists()) {
        // Extract name from email if displayName is not available
        const nameFromEmail = user.email ? user.email.split("@")[0].replace(/[^a-zA-Z0-9]/g, " ") : ""
        // Capitalize first letter of each word
        const formattedName = nameFromEmail
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ")

        await setDoc(userDocRef, {
          name: user.displayName || formattedName,
          email: user.email || "",
          photoURL: user.photoURL || "",
          createdAt: serverTimestamp(),
          role: "user", // Default role for new users
          authProvider: "google",
        })
      }

      // Set user in context
      setCurrentUser(user)
      setRole(role)
      setLibraryId(libraryId || "")

      // Set localStorage items
      localStorage.setItem("userId", user.uid)
      localStorage.setItem("role", role)
      localStorage.setItem("libraryId", libraryId || "")

      // Redirect based on role
      if (role === "admin") {
        navigate("/admin-dashboard")
      } else if (role === "owner") {
        navigate("/owner-dashboard")
      } else {
        navigate("/dashboard")
      }
    } catch (err) {
      console.error("Google sign-in failed:", err)
      setError("Google sign-in failed. Please try again.")
      setIsLoading(false)
    }
  }

  const handleForgotPassword = async () => {
    setForgotError("")
    setResetMessage("")
    setIsLoading(true)

    if (!forgotEmail) {
      setForgotError("Please enter your email.")
      setIsLoading(false)
      return
    }

    try {
      await sendPasswordResetEmail(getAuth(), forgotEmail)
      setResetMessage("Password reset email sent! Please check your inbox.")
      setIsModalOpen(false)
    } catch (err) {
      console.error("Failed to send password reset email:", err)
      setForgotError("Failed to send password reset email. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-900 to-purple-900 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <div className="bg-white bg-opacity-10 backdrop-filter backdrop-blur-lg rounded-2xl shadow-xl overflow-hidden">
          <div className="px-4 py-5 sm:p-6">
            <h2 className="text-center text-3xl font-extrabold text-white mb-6">Welcome Back</h2>
            <form onSubmit={handleLogin} className="space-y-6" aria-live="assertive">
              <div>
                <label htmlFor="email" className="sr-only">
                  Email address
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaEnvelope className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white bg-opacity-20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-blue-900 focus:ring-white focus:border-white sm:text-sm"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLock className="h-5 w-5 text-gray-400" aria-hidden="true" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white bg-opacity-20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-blue-900 focus:ring-white focus:border-white sm:text-sm"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-gray-400 hover:text-gray-300 focus:outline-none focus:text-gray-300"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <FaEyeSlash className="h-5 w-5" /> : <FaEye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-300">
                    Remember me
                  </label>
                </div>

                <div className="text-sm">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(true)}
                    className="font-medium text-blue-300 hover:text-blue-200 focus:outline-none focus:underline transition ease-in-out duration-150"
                  >
                    Forgot your password?
                  </button>
                </div>
              </div>

              {error && <p className="text-sm text-red-400 mt-2">{error}</p>}

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
                    isLoading
                      ? "bg-blue-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  } transition duration-150 ease-in-out`}
                >
                  {isLoading ? (
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : null}
                  {isLoading ? "Logging in..." : "Sign in"}
                </button>
              </div>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 py-1 bg-white bg-opacity-10 text-white rounded-full">Or</span>
                </div>
              </div>

              <div className="mt-6 space-y-4">
                {/* Google Sign In Button */}
                <button
                  onClick={handleGoogleSignIn}
                  disabled={isLoading}
                  className={`w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 ${
                    isLoading ? "opacity-70 cursor-not-allowed" : ""
                  } transition duration-150 ease-in-out`}
                >
                  {isLoading ? (
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-700"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    <img src="/images/google.png" alt="Google" className="h-5 w-5 mr-2" />
                  )}
                  Continue with Google
                </button>

                {/* Sign Up Button */}
                <button
                  onClick={() => setIsSignupModalOpen(true)}
                  disabled={isLoading}
                  className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                    isLoading
                      ? "bg-purple-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                  } transition duration-150 ease-in-out`}
                >
                  <FaUser className="mr-2 h-5 w-5" />
                  Sign up
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div
          className="fixed inset-0 z-10 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                    Reset Password
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      Enter your email address and we'll send you a link to reset your password.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
                {forgotError && <p className="mt-2 text-sm text-red-600">{forgotError}</p>}
                {resetMessage && <p className="mt-2 text-sm text-green-600">{resetMessage}</p>}
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:col-start-2 sm:text-sm"
                    onClick={handleForgotPassword}
                    disabled={isLoading}
                  >
                    {isLoading ? "Sending..." : "Send Reset Link"}
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                    onClick={() => setIsModalOpen(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <SignupModal isOpen={isSignupModalOpen} onClose={() => setIsSignupModalOpen(false)} />

      <AccountLinkingModal
        isOpen={isLinkingModalOpen}
        onClose={() => setIsLinkingModalOpen(false)}
        email={pendingEmail || email}
        authMethods={authMethods}
      />
    </div>
  )
}

export default Login
